<template>
    <v-dialog v-model="dialogVisible" max-width="900px">
        <v-card>
            <v-card-title>
                <span class="headline">
                    {{
                        isEdited
                            ? $t("modules_web.edit_module")
                            : $t("modules_web.add_module")
                    }}
                </span>
            </v-card-title>

            <v-card-text>
                <v-container>
                    <v-form ref="form" v-model="valid" lazy-validation>
                        <v-row>
                            <v-col cols="12" class="mb-4">
                                <v-btn-toggle
                                    v-model="activeLang"
                                    mandatory
                                    dense
                                >
                                    <v-btn
                                        value="base"
                                        small
                                        :class="
                                            activeLang === 'base'
                                                ? 'primary white--text'
                                                : ''
                                        "
                                    >
                                        {{
                                            $t("modules_web.general") ||
                                            "Obecné"
                                        }}
                                    </v-btn>

                                    <v-btn
                                        value="cz"
                                        small
                                        :class="
                                            activeLang === 'cz'
                                                ? 'primary white--text'
                                                : ''
                                        "
                                    >
                                        CZ
                                    </v-btn>

                                    <v-btn
                                        v-for="lang in filteredLanguages"
                                        :key="lang.code"
                                        :value="lang.code"
                                        small
                                        :class="
                                            activeLang === lang.code
                                                ? 'primary white--text'
                                                : ''
                                        "
                                    >
                                        {{ lang.name }} ({{ lang.code }})
                                    </v-btn>
                                </v-btn-toggle>
                            </v-col>
                        </v-row>
                        <!-- Base fields -->
                        <template v-if="activeLang === 'base'">
                            <v-row>
                                <v-col cols="12" md="6" class="py-0">
                                    <v-select
                                        v-model="formData.module_name_id"
                                        :items="moduleNames"
                                        :label="$t('modules_web.module')"
                                        item-text="name"
                                        item-value="id"
                                        :rules="moduleRules"
                                        required
                                    ></v-select>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6" class="py-0">
                                    <v-menu
                                        v-model="dateMenus.cz"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="auto"
                                    >
                                        <template
                                            v-slot:activator="{ on, attrs }"
                                        >
                                            <v-text-field
                                                v-model="formData.tag_date"
                                                :label="
                                                    $t(
                                                        'modules_web.tag_date_cz'
                                                    )
                                                "
                                                prepend-icon="mdi-calendar"
                                                readonly
                                                v-bind="attrs"
                                                v-on="on"
                                            ></v-text-field>
                                        </template>
                                        <v-date-picker
                                            v-model="formData.tag_date"
                                            no-title
                                            @input="dateMenus.cz = false"
                                        ></v-date-picker>
                                    </v-menu>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="4" class="py-0">
                                    <v-text-field
                                        v-model="formData.icon_tag"
                                        :label="$t('modules_web.icon_tag')"
                                        :counter="100"
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="12" md="4" class="py-0">
                                    <input
                                        type="file"
                                        ref="iconInput"
                                        accept="image/*"
                                        style="display: none"
                                        @change="onIconNativeChange"
                                    />
                                    <v-text-field
                                        :label="$t('modules_web.icon_upload')"
                                        prepend-icon="mdi-upload"
                                        v-model="iconFileName"
                                        readonly
                                        @click="triggerIconSelect"
                                    >
                                        <template
                                            v-slot:append-outer
                                            v-if="formData.icon_image"
                                        >
                                            <v-icon
                                                class="itb-btn-delete"
                                                @click.stop="clearIconImage"
                                            >
                                                mdi-delete-outline
                                            </v-icon>
                                        </template>
                                    </v-text-field>
                                </v-col>

                                <v-col
                                    cols="12"
                                    md="4"
                                    v-if="iconPreviewUrl"
                                    class="py-0"
                                >
                                    <v-img
                                        :src="iconPreviewUrl"
                                        max-width="100"
                                        class="elevation-3"
                                        contain
                                    ></v-img>
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col cols="12" md="4" class="py-0">
                                    <input
                                        type="file"
                                        ref="headerInput"
                                        accept="image/*"
                                        style="display: none"
                                        @change="onHeaderNativeChange"
                                    />
                                    <v-text-field
                                        :label="$t('modules_web.header_upload')"
                                        prepend-icon="mdi-upload"
                                        v-model="headerFileName"
                                        readonly
                                        @click="triggerHeaderSelect"
                                    >
                                        <template
                                            v-slot:append-outer
                                            v-if="formData.header_image"
                                        >
                                            <v-icon
                                                class="itb-btn-delete"
                                                @click.stop="clearHeaderImage"
                                            >
                                                mdi-delete-outline
                                            </v-icon>
                                        </template>
                                    </v-text-field>
                                </v-col>

                                <v-col
                                    cols="12"
                                    md="8"
                                    v-if="headerPreviewUrl"
                                    class="py-0"
                                >
                                    <v-img
                                        :src="headerPreviewUrl"
                                        max-width="300"
                                        class="elevation-3"
                                        contain
                                    ></v-img>
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col cols="12" class="py-0">
                                    <v-switch
                                        v-model="formData.is_active"
                                        :label="$t('general.active')"
                                        color="green"
                                    ></v-switch>
                                </v-col>
                            </v-row>
                        </template>

                        <!-- Language fields -->
                        <template v-if="activeLang === 'cz'">
                            <v-row>
                                <v-col cols="12" md="6" class="py-0">
                                    <v-text-field
                                        v-model="formData.title"
                                        :label="$t('modules_web.title_cz')"
                                        :rules="titleRules"
                                        required
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="12" class="py-0">
                                    <v-text-field
                                        v-model="formData.subtitle"
                                        :label="$t('modules_web.subtitle_cz')"
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="12" md="6" class="py-0">
                                    <v-text-field
                                        v-model="formData.tag_text"
                                        :label="$t('modules_web.tag_text_cz')"
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="12" class="py-0">
                                    <label class="mb-2 d-block">
                                        {{ $t("modules_web.description_cz") }}
                                    </label>
                                    <div class="d-flex mb-1" style="gap: 4px">
                                        <v-btn
                                            small
                                            icon
                                            @click="undoCz"
                                            class="itb-custom-btn"
                                        >
                                            <v-icon small>mdi-undo</v-icon>
                                        </v-btn>
                                        <v-btn
                                            small
                                            icon
                                            :color="
                                                showCodeCz
                                                    ? 'primary'
                                                    : undefined
                                            "
                                            @click="toggleCodeCz"
                                            class="itb-custom-btn"
                                        >
                                            <v-icon small>mdi-code-tags</v-icon>
                                        </v-btn>
                                    </div>
                                    <vue-editor
                                        v-if="!showCodeCz"
                                        ref="czEditor"
                                        v-model="formData.description"
                                        :editorToolbar="customToolbar"
                                    />
                                    <v-textarea
                                        v-else
                                        v-model="formData.description"
                                        rows="8"
                                        auto-grow
                                        outlined
                                    ></v-textarea>
                                    <div
                                        v-if="!formData.description"
                                        class="error--text text-caption mt-1"
                                    >
                                        {{
                                            $t(
                                                "modules_web.description_required"
                                            )
                                        }}
                                    </div>
                                </v-col>

                                <v-col cols="12" md="6" class="py-0 mt-5">
                                    <v-text-field
                                        v-model="formData.btn_link"
                                        :label="$t('modules_web.btn_link')"
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="12" md="6" class="py-0 mt-5">
                                    <v-text-field
                                        v-model="formData.btn_text"
                                        :label="$t('modules_web.btn_text_cz')"
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                        </template>

                        <template v-else>
                            <v-row
                                v-for="lang in filteredLanguages"
                                v-if="activeLang === lang.code"
                                :key="`lang-${lang.code}`"
                            >
                                <v-col cols="12" md="6" class="py-0">
                                    <v-text-field
                                        v-model="formData[`title_${lang.code}`]"
                                        :label="
                                            $t('modules_web.title_lang') +
                                            ' ' +
                                            lang.name +
                                            ' (' +
                                            lang.code +
                                            ')'
                                        "
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" class="py-0">
                                    <v-text-field
                                        class="mt-2"
                                        v-model="
                                            formData[`subtitle_${lang.code}`]
                                        "
                                        :label="
                                            $t('modules_web.subtitle_lang') +
                                            ' ' +
                                            lang.name +
                                            ' (' +
                                            lang.code +
                                            ')'
                                        "
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6" class="py-0">
                                    <v-text-field
                                        class="mt-2"
                                        v-model="
                                            formData[`tag_text_${lang.code}`]
                                        "
                                        :label="
                                            $t('modules_web.tag_text_lang') +
                                            ' ' +
                                            lang.name +
                                            ' (' +
                                            lang.code +
                                            ')'
                                        "
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" class="py-0">
                                    <label class="mt-2 d-block">
                                        {{
                                            $t("modules_web.description_lang") +
                                            " " +
                                            lang.name +
                                            " (" +
                                            lang.code +
                                            ")"
                                        }}
                                    </label>

                                    <div class="d-flex mb-1" style="gap: 4px">
                                        <v-btn
                                            small
                                            icon
                                            @click="undoLang(lang.code)"
                                            class="itb-custom-btn"
                                        >
                                            <v-icon small>mdi-undo</v-icon>
                                        </v-btn>
                                        <v-btn
                                            small
                                            icon
                                            :color="
                                                showCode[lang.code]
                                                    ? 'primary'
                                                    : undefined
                                            "
                                            @click="toggleCodeLang(lang.code)"
                                            class="itb-custom-btn"
                                        >
                                            <v-icon small>mdi-code-tags</v-icon>
                                        </v-btn>
                                    </div>

                                    <vue-editor
                                        v-if="!showCode[lang.code]"
                                        :ref="`editor_${lang.code}`"
                                        class="mt-2"
                                        v-model="
                                            formData[`description_${lang.code}`]
                                        "
                                        :editorToolbar="customToolbar"
                                    />

                                    <v-textarea
                                        v-else
                                        v-model="
                                            formData[`description_${lang.code}`]
                                        "
                                        rows="8"
                                        auto-grow
                                        outlined
                                    ></v-textarea>
                                </v-col>

                                <v-col cols="12" md="6" class="py-0 mt-5">
                                    <v-text-field
                                        class="mt-2"
                                        v-model="
                                            formData[`btn_link_${lang.code}`]
                                        "
                                        :label="
                                            $t('modules_web.btn_link_lang') +
                                            ' ' +
                                            lang.name +
                                            ' (' +
                                            lang.code +
                                            ')'
                                        "
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="6" class="py-0 mt-5">
                                    <v-text-field
                                        class="mt-2"
                                        v-model="
                                            formData[`btn_text_${lang.code}`]
                                        "
                                        :label="
                                            $t('modules_web.btn_text_lang') +
                                            ' ' +
                                            lang.name +
                                            ' (' +
                                            lang.code +
                                            ')'
                                        "
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                        </template>
                    </v-form>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="closeDialog">
                    {{ $t("general.cancel") }}
                </v-btn>
                <v-btn
                    color="blue darken-1"
                    text
                    @click="saveModule"
                    :disabled="!valid"
                >
                    {{ $t("general.save") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { VueEditor } from "vue2-editor";
export default {
    name: "ModulesWebForm",

    props: {
        module: {
            type: Object,
            required: true,
        },
        languages: {
            type: Array,
            required: true,
        },
        moduleNames: {
            type: Array,
            default: () => [],
        },
        visible: {
            type: Boolean,
            default: false,
        },
        isEdited: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            valid: true,
            activeLang: "base",
            formData: {
                id: null,
                module_name_id: null,
                title: "",
                subtitle: "",
                tag_text: "",
                tag_date: null,
                description: "",
                btn_link: "",
                btn_text: "",
                icon_tag: "",
                icon_image: "",
                is_active: 0,
            },
            iconFile: null,
            iconFileName: "",
            headerFile: null,
            headerFileName: "",
            dateMenus: {
                cz: false,
            },
            showCodeCz: false,
            showCode: {},
            customToolbar: [
                ["bold", "italic"],
                [{ header: [3, 4, false] }],
                [{ list: "bullet" }],
            ],
            moduleRules: [(v) => !!v || this.$t("modules_web.module_required")],
            titleRules: [(v) => !!v || this.$t("modules_web.title_required")],
            descriptionRules: [
                (v) => !!v || this.$t("modules_web.description_required"),
            ],
        };
    },

    components: {
        VueEditor,
    },

    computed: {
        filteredLanguages() {
            return this.languages.filter((l) => l.code !== "cz");
        },
        dialogVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                if (!value) {
                    this.$emit("close");
                }
            },
        },
        iconPreviewUrl() {
            if (!this.formData.icon_image) return null;
            return this.formData.icon_image;
        },
        headerPreviewUrl() {
            if (!this.formData.header_image) return null;
            return this.formData.header_image;
        },
    },

    watch: {
        visible(val) {
            if (val) {
                this.activeLang = "base";
                this.iconFile = null;

                if (this.isEdited) {
                    this.formData = JSON.parse(JSON.stringify(this.module));
                    this.ensureDynamicFields();
                } else {
                    this.resetForm();
                }
            }
        },
        languages() {
            this.ensureDynamicFields();
        },
    },

    methods: {
        toggleCodeCz() {
            this.showCodeCz = !this.showCodeCz;
        },
        undoCz() {
            const editor = this.$refs.czEditor;
            if (editor && editor.quill && editor.quill.history) {
                editor.quill.history.undo();
            }
        },
        toggleCodeLang(code) {
            if (this.showCode[code] === undefined) {
                this.$set(this.showCode, code, false);
            }
            this.showCode[code] = !this.showCode[code];
        },
        undoLang(code) {
            const refName = `editor_${code}`;
            let editor = this.$refs[refName];

            if (Array.isArray(editor)) {
                editor = editor[0];
            }

            if (editor && editor.quill && editor.quill.history) {
                editor.quill.history.undo();
            }
        },
        ensureDynamicFields() {
            const data = { ...this.formData };

            this.filteredLanguages.forEach((lang) => {
                const tField = `title_${lang.code}`;
                const sField = `subtitle_${lang.code}`;
                const tagField = `tag_text_${lang.code}`;
                const dField = `description_${lang.code}`;
                const btnField = `btn_text_${lang.code}`;
                const linkField = `btn_link_${lang.code}`;

                if (data[tField] === undefined) data[tField] = "";
                if (data[sField] === undefined) data[sField] = "";
                if (data[tagField] === undefined) data[tagField] = "";
                if (data[dField] === undefined) data[dField] = "";
                if (data[btnField] === undefined) data[btnField] = "";
                if (data[linkField] === undefined) data[linkField] = "";

                if (this.showCode[lang.code] === undefined) {
                    this.$set(this.showCode, lang.code, false);
                }
            });

            if (data.is_active === undefined || data.is_active === null) {
                data.is_active = 0;
            }

            this.formData = data;
        },

        resetForm() {
            const base = {
                id: null,
                module_name_id: null,
                title: "",
                subtitle: "",
                tag_text: "",
                tag_date: null,
                description: "",
                btn_link: "",
                btn_text: "",
                icon_tag: "",
                icon_image: "",
                is_active: 0,
            };

            this.languages.forEach((lang) => {
                if (lang.code === "cz") return;
                base[`title_${lang.code}`] = "";
                base[`subtitle_${lang.code}`] = "";
                base[`tag_text_${lang.code}`] = "";
                base[`description_${lang.code}`] = "";
                base[`btn_text_${lang.code}`] = "";
                base[`btn_link_${lang.code}`] = "";

                this.$set(this.showCode, lang.code, false);
            });

            this.formData = base;
            this.iconFile = null;
            this.dateMenus.cz = false;

            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },

        closeDialog() {
            this.$emit("close");
            this.resetForm();
        },

        async saveModule() {
            if (this.$refs.form.validate()) {
                try {
                    if (this.isEdited) {
                        await this.updateModule();
                    } else {
                        await this.createModule();
                    }
                    this.closeDialog();
                } catch (error) {
                    console.error(error.response?.data || error);
                    this.$emit("snackMessage", "general.error", "error");
                }
            }
        },

        async createModule() {
            const response = await axios.post("/modules-web", this.formData, {
                headers: {
                    Authorization:
                        "Bearer " +
                        this.$store.state.tokenData.user.access_token,
                },
            });

            const module = response.data.module || response.data;
            this.$emit("addModule", module);
            this.$emit(
                "snackMessage",
                "modules_web.created_successfully",
                "success"
            );
        },

        async updateModule() {
            const response = await axios.put(
                `/modules-web/${this.module.id}`,
                this.formData,
                {
                    headers: {
                        Authorization:
                            "Bearer " +
                            this.$store.state.tokenData.user.access_token,
                    },
                }
            );

            const module = response.data.module || response.data;
            this.$emit("updateModule", module);
            this.$emit(
                "snackMessage",
                "modules_web.updated_successfully",
                "success"
            );
        },

        triggerIconSelect() {
            if (this.$refs.iconInput) {
                this.$refs.iconInput.click();
            }
        },

        onIconNativeChange(e) {
            const file =
                e.target.files && e.target.files[0] ? e.target.files[0] : null;
            if (!file) return;

            this.iconFile = file;
            this.iconFileName = file.name;

            this.uploadIcon(file);

            e.target.value = "";
        },

        async uploadIcon(file) {
            if (!file) return;

            const fd = new FormData();
            fd.append("icon_image", file);

            try {
                const response = await axios.post(
                    "/modules-web/upload-icon",
                    fd,
                    {
                        headers: {
                            Authorization:
                                "Bearer " +
                                this.$store.state.tokenData.user.access_token,
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );

                this.formData.icon_image = response.data.path;

                this.$emit(
                    "snackMessage",
                    "modules_web.icon_uploaded_successfully",
                    "success"
                );
            } catch (error) {
                console.error(error.response?.data || error);
                this.$emit("snackMessage", "general.error", "error");
            }
        },

        clearIconImage() {
            this.formData.icon_image = null;
            this.iconFile = null;
            this.iconFileName = "";

            if (this.$refs.iconInput) {
                this.$refs.iconInput.value = "";
            }
        },

        triggerHeaderSelect() {
            if (this.$refs.headerInput) {
                this.$refs.headerInput.click();
            }
        },

        onHeaderNativeChange(e) {
            const file =
                e.target.files && e.target.files[0] ? e.target.files[0] : null;
            if (!file) return;

            this.headerFile = file;
            this.headerFileName = file.name;

            this.uploadHeader(file);
            e.target.value = "";
        },

        async uploadHeader(file) {
            if (!file) return;

            const fd = new FormData();
            fd.append("header_image", file);

            try {
                const res = await axios.post("/modules-web/upload-header", fd, {
                    headers: {
                        Authorization:
                            "Bearer " +
                            this.$store.state.tokenData.user.access_token,
                        "Content-Type": "multipart/form-data",
                    },
                });

                this.formData.header_image = res.data.path;

                this.$emit(
                    "snackMessage",
                    "modules_web.header_uploaded_successfully",
                    "success"
                );
            } catch (error) {
                console.error(error);
                this.$emit("snackMessage", "general.error", "error");
            }
        },
        clearHeaderImage() {
            this.formData.header_image = null;
            this.headerFile = null;
            this.headerFileName = "";

            if (this.$refs.headerInput) {
                this.$refs.headerInput.value = "";
            }
        },
    },
};
</script>

<style scoped>
.itb-custom-btn {
    background: #1976d2;
    color: white !important;
    border-radius: 4px;
    padding: 4px 8px;
    border: none;
    cursor: pointer;
    font-weight: bold;
    margin-right: 4px;
}

.itb-custom-btn i {
    color: white !important;
}

.itb-custom-btn:hover {
    background: #1565c0;
}

.itb-btn-delete {
    color: #f44336 !important;
    cursor: pointer;
}

.itb-btn-delete:hover {
    color: #ae0f03 !important;
}
</style>
